// Phôi (ITEMS §3.2, §3.3). P0: 2 loại vũ khí (Kiếm, Đao), 2 phôi mỗi ô, Pháp Bảo thường.
// Icon chỉ đặt khi client tham khảo có ảnh; còn lại ô dùng placeholder (UI_SPEC §10.2).
import type { BaseDef, SlotKind } from './types';
import { UNIQUES } from './uniques';

export const BASES: readonly BaseDef[] = [
  // Vũ khí (scaling Tam Nguyên theo ITEMS §3.3)
  {
    id: 'base.sword',
    slot: 'weapon',
    weaponType: 'sword',
    implicit: [],
    scaling: { body: 'B', spirit: 'B', mind: 'C' },
    element: 'metal',
    phase: 'P0',
  },
  {
    id: 'base.saber',
    slot: 'weapon',
    weaponType: 'saber',
    implicit: [],
    scaling: { body: 'S', spirit: 'E', mind: 'E' },
    element: 'metal',
    phase: 'P0',
  },
  // Áo
  {
    id: 'base.daoRobe',
    slot: 'robe',
    implicit: [{ affix: 'manaMax', value: 8 }],
    element: 'wood',
    phase: 'P0',
  },
  {
    id: 'base.armorRobe',
    slot: 'robe',
    implicit: [{ affix: 'defRating', value: 10 }],
    element: 'earth',
    phase: 'P0',
  },
  {
    id: 'base.spellRobe',
    slot: 'robe',
    implicit: [{ affix: 'resAll', value: 6 }],
    element: 'water',
    phase: 'P0',
  },
  // Nón
  { id: 'base.daoCrown', slot: 'helm', implicit: [{ affix: 'cdr', value: 3 }], element: 'wood', phase: 'P0' },
  {
    id: 'base.warHelm',
    slot: 'helm',
    implicit: [{ affix: 'killMoveReduce', value: 5 }],
    element: 'metal',
    phase: 'P0',
  },
  // Giày
  {
    id: 'base.cloudBoots',
    slot: 'boots',
    implicit: [{ affix: 'dodgeIframe', value: 20 }],
    element: 'water',
    phase: 'P0',
  },
  {
    id: 'base.warBoots',
    slot: 'boots',
    implicit: [{ affix: 'defRating', value: 6 }],
    element: 'earth',
    phase: 'P0',
  },
  // Găng
  {
    id: 'base.swordSleeve',
    slot: 'gloves',
    implicit: [{ affix: 'parryWindow', value: 10 }],
    element: 'metal',
    phase: 'P0',
  },
  {
    id: 'base.ironGloves',
    slot: 'gloves',
    implicit: [{ affix: 'poiseDmg', value: 5 }],
    element: 'earth',
    phase: 'P0',
  },
  // Dây chuyền
  {
    id: 'base.jadePendant',
    slot: 'necklace',
    icon: 'thanh-tam-boi',
    implicit: [{ affix: 'qiRate', value: 3 }],
    element: 'wood',
    phase: 'P0',
  },
  {
    id: 'base.sachet',
    slot: 'necklace',
    implicit: [{ affix: 'pillToxRes', value: 5 }],
    element: 'wood',
    phase: 'P0',
  },
  // Nhẫn
  {
    id: 'base.jadeRing',
    slot: 'ring',
    icon: 'tram-tinh-nhan',
    implicit: [{ affix: 'manaRegen', value: 6 }],
    element: 'water',
    phase: 'P0',
  },
  {
    id: 'base.ironRing',
    slot: 'ring',
    implicit: [{ affix: 'atkFlat', value: 1 }],
    element: 'metal',
    phase: 'P0',
  },
  // Quần
  {
    id: 'base.warPants',
    slot: 'pants',
    implicit: [{ affix: 'hpPct', value: 3 }],
    element: 'earth',
    phase: 'P0',
  },
  {
    id: 'base.silkPants',
    slot: 'pants',
    icon: 'thanh-van-dai',
    implicit: [{ affix: 'dodgeRecharge', value: 6 }],
    element: 'wood',
    phase: 'P0',
  },
  // Pháp Bảo thường (ITEMS §5.3: trước KĐ ô Pháp Bảo chứa pháp bảo thường)
  {
    id: 'base.mirror',
    slot: 'treasure',
    icon: 'tram-uyen-kinh',
    implicit: [{ affix: 'skillDmg', value: 4 }],
    element: 'water',
    phase: 'P0',
  },
  {
    id: 'base.pearl',
    slot: 'treasure',
    implicit: [{ affix: 'qiRate', value: 2 }],
    element: 'light',
    phase: 'P0',
  },
  // Phôi riêng của độc bản P0 (không roll ngẫu nhiên)
  {
    id: 'base.fallenLeafSword',
    slot: 'weapon',
    weaponType: 'sword',
    icon: 'lac-diep-kiem',
    implicit: [],
    scaling: { body: 'B', spirit: 'B', mind: 'C' },
    element: 'wood',
    phase: 'P0',
  },
  {
    id: 'base.coldJadeArmor',
    slot: 'robe',
    icon: 'han-ngoc-giap',
    implicit: [{ affix: 'defRating', value: 10 }],
    element: 'water',
    phase: 'P0',
  },
  {
    id: 'base.abyssMirror',
    slot: 'treasure',
    icon: 'tram-uyen-kinh',
    implicit: [{ affix: 'skillDmg', value: 4 }],
    element: 'water',
    phase: 'P0',
  },
];

// Phôi riêng của độc bản: suy từ bảng UNIQUES (một nguồn), không chép tay.
const UNIQUE_BASES = new Set(UNIQUES.map((u) => u.base));

export function baseDef(id: string): BaseDef {
  const d = BASES.find((x) => x.id === id);
  if (!d) throw new RangeError(`unknown base: ${id}`);
  return d;
}

export function isBaseId(v: unknown): v is string {
  return typeof v === 'string' && BASES.some((b) => b.id === v);
}

/** Phôi rơi ngẫu nhiên được ở ô này (P0, không gồm phôi của độc bản). */
export function basesFor(slot: SlotKind): readonly BaseDef[] {
  return BASES.filter((b) => b.slot === slot && b.phase === 'P0' && !UNIQUE_BASES.has(b.id));
}
